import { ClassicEditor } from 'ckeditor5'
import { MutableRefObject } from 'react'
import { CreateKeyHandlerParams } from '../types'
import { shouldHandleKeyEvent } from './shortcutDetection.utils'

/**
 * Utility to safely get the editor instance and log a warning if not available.
 * @param editorRef - The ref object for the editor
 * @param operation - A string describing the operation (for logging)
 * @returns The editor instance or null if not available
 */
export const getEditorReference = (
    editorRef: MutableRefObject<ClassicEditor | undefined>,
    operation: string
) => {
    const editor = editorRef.current
    if (!editor) {
        console.warn(`No editor reference available for ${operation} operation`)
        return null
    }
    return editor
}

/**
 * Creates a smart key handler that prevents duplicate calls and uses optimal event timing.
 * The handler processes keyboard events and executes the first matching handler from the provided list.
 *
 * @param eventType - Whether this handles 'down' or 'up' key events
 * @param params - Configuration parameters for the key handler
 * @returns A key event handler function
 */
export const createKeyHandler = (
    eventType: 'down' | 'up',
    params: CreateKeyHandlerParams
): ((event: React.KeyboardEvent<HTMLElement>) => void) => {
    const {
        handlers,
        detectContentChanges,
        unsavedData,
        originalText,
        nodeToAdd,
        previousContentItemViewNodes,
        initialSuggestions,
        hasDeletedWholeText,
        treeRef,
    } = params

    return (event: React.KeyboardEvent<HTMLElement>) => {
        const shouldHandle = shouldHandleKeyEvent(event, eventType)

        if (!shouldHandle) return

        const changeDetection = detectContentChanges({
            unsavedData,
            originalText,
            nodeToAdd,
            previousContentItemViewNodes,
            initialSuggestions,
            hasDeletedWholeText,
            isEditingPlainText: true,
            textContentElementToAdd: null,
        })

        // Execute the first handler that returns true
        for (const handler of handlers) {
            const result = handler({
                event,
                textElementText: originalText,
                changeDetection,
                treeRef,
            })
            if (result) break
        }
    }
}
